/**
 * The controlled Select core shared by every Facebook Page / Meta Pixel picker (#6182).
 *
 * Exists to own ONE invariant in ONE place: the value is always controlled — `''` when nothing is
 * selected, never `undefined`. Flipping between uncontrolled (undefined) and controlled (string)
 * makes Radix lose the Portal's DOM node on cleanup and crash with a removeChild NotFoundError
 * (#4607); that bug re-entered three of the four hand-rolled picker copies before #6163 P0-3
 * caught it. A picker built on this component cannot get the discipline wrong.
 *
 * Deliberately small: the surrounding chrome (bordered cards, warning alerts, loading/empty
 * branches, footnotes) legitimately differs between the wizard gate and the account modal and
 * stays with the callers; the data logic lives in `hooks/useMetaAssetPicker`.
 */

import type { ReactNode } from 'react';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export interface MetaAssetSelectOption {
  value: string;
  label: ReactNode;
}

export function MetaAssetSelect({
  id,
  value,
  onValueChange,
  disabled = false,
  placeholder,
  triggerValue,
  triggerClassName,
  options,
  emptyContent,
}: {
  id?: string;
  /** Current selection — null/undefined renders as the controlled unselected state (`''`). */
  value: string | null | undefined;
  onValueChange: (value: string) => void;
  disabled?: boolean;
  placeholder: string;
  /** Optional explicit trigger text; omitted = Radix renders the selected item's label. */
  triggerValue?: ReactNode;
  triggerClassName?: string;
  options: MetaAssetSelectOption[];
  /** Rendered inside the dropdown when `options` is empty (e.g. "No Facebook Pages found."). */
  emptyContent?: ReactNode;
}) {
  return (
    <Select value={value ?? ''} onValueChange={onValueChange} disabled={disabled}>
      <SelectTrigger id={id} className={triggerClassName}>
        <SelectValue placeholder={placeholder}>{triggerValue}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
        {options.length === 0 && emptyContent}
      </SelectContent>
    </Select>
  );
}
